import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";

export interface VisualMap {
  id: string;
  name: string;
  macro_statement: string;
}

export interface VisualSeed {
  id: string;
  name: string;
  intent_family: string[];
}

export interface OutlineSection {
  type: string;
  header: string | null;
  h3s?: Array<{ header: string; source_paa?: string }>;
  section_type?: string;
}

export interface VisualNode {
  id: string;
  title: string;
  slug: string;
  document_role: string;
  primary_entity_raw: string;
  seed_id: string;
  engine_stage: string | null;
  quality_score: number | null;
  word_count: number | null;
  outline: OutlineSection[] | null;
  final_html: string | null;
}

export function useMapVisualData(mapId: string | null) {
  const [maps, setMaps] = useState<VisualMap[]>([]);
  const [seeds, setSeeds] = useState<VisualSeed[]>([]);
  const [nodes, setNodes] = useState<VisualNode[]>([]);
  const [loading, setLoading] = useState(false);

  // Fetch all maps (for dropdown)
  const fetchMaps = useCallback(async () => {
    const { data } = await (supabase as any)
      .from("thematic_maps")
      .select("id, name, macro_statement")
      .order("created_at", { ascending: false });
    setMaps((data || []).map((m: any) => ({
      id: m.id, name: m.name, macro_statement: m.macro_statement || "",
    })));
  }, []);

  // Fetch seeds + nodes for selected map
  const fetchMapData = useCallback(async (id: string) => {
    setLoading(true);
    try {
      const [seedsRes, nodesRes] = await Promise.all([
        (supabase as any)
          .from("thematic_seeds")
          .select("id, name, intent_family")
          .eq("map_id", id)
          .order("sort_order", { ascending: true }),
        (supabase as any)
          .from("thematic_nodes")
          .select("id, title, slug, document_role, primary_entity_raw, seed_id")
          .eq("map_id", id),
      ]);

      setSeeds((seedsRes.data || []).map((s: any) => ({
        id: s.id, name: s.name, intent_family: s.intent_family || [],
      })));

      // Fetch content status separately (different table)
      const nodeIds = (nodesRes.data || []).map((n: any) => n.id);
      let contentMap = new Map<string, { engine_stage: string | null; quality_score: number | null; word_count: number | null; outline: OutlineSection[] | null; final_html: string | null }>();

      if (nodeIds.length > 0) {
        const { data: contentData } = await (supabase as any)
          .from("thematic_node_content")
          .select("node_id, engine_stage, quality_score, outline, final_html")
          .in("node_id", nodeIds);

        for (const c of contentData || []) {
          const html = c.final_html || "";
          const wc = html ? html.replace(/<[^>]+>/g, " ").split(/\s+/).filter(Boolean).length : 0;
          const outlineSections = c.outline?.sections || c.outline || null;
          contentMap.set(c.node_id, {
            engine_stage: c.engine_stage || null,
            quality_score: c.quality_score || null,
            word_count: wc > 0 ? wc : null,
            outline: Array.isArray(outlineSections) ? outlineSections : null,
            final_html: c.final_html || null,
          });
        }
      }

      setNodes((nodesRes.data || []).map((n: any) => {
        const content = contentMap.get(n.id);
        return {
          id: n.id,
          title: n.title,
          slug: n.slug,
          document_role: n.document_role,
          primary_entity_raw: n.primary_entity_raw || "",
          seed_id: n.seed_id,
          engine_stage: content?.engine_stage || "pending",
          quality_score: content?.quality_score || null,
          word_count: content?.word_count || null,
          outline: content?.outline || null,
          final_html: content?.final_html || null,
        };
      }));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchMaps(); }, [fetchMaps]);
  useEffect(() => { if (mapId) fetchMapData(mapId); }, [mapId, fetchMapData]);

  return { maps, seeds, nodes, loading, refetch: () => mapId && fetchMapData(mapId) };
}
